// EventOperatorsSelection – assign operators to an event.

import React, { JSX, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { EventOperatorRow } from '../../../../types/EventTypes';
import { formatTs } from '../../../helpers/helpers';
import RefreshButton from '../../shared/RefreshButton';
import LoadingState from '../../shared/LoadingState';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import InlineSpinner from '../../shared/InlineSpinner';
import { dispatchToast, dispatchToastError } from '../utils';

// ---- Types ----

type EventRow = {
    eventId: string;
    name?: string;
    status?: string;
    createdAt?: number;
    startAt?: number;
    endAt?: number;
    [key: string]: unknown;
};

type OperatorUser = {
    userId: string;
    preferred_username?: string;
    username?: string;
    email?: string;
};

// ---- Helpers ----

function formatUserLabel(u: OperatorUser): string {
    const preferred = String(u.preferred_username ?? '').trim();
    if (preferred) return preferred;
    if (u.email) return u.email;
    if (u.username) return u.username;
    return u.userId;
}

function formatAssignedOperatorLabel(op: EventOperatorRow): string {
    const preferred = String(op.preferred_username ?? '').trim();
    if (preferred) return preferred;
    const email = String(op.email ?? '').trim();
    if (email) return email;
    const username = String(op.username ?? '').trim();
    if (username) return username;
    return op.userId;
}

// ---- Component ----

export default function EventOperatorsSection(): JSX.Element {
    const { isAdmin } = useAuth();

    const [events, setEvents] = useState<EventRow[]>([]);
    const [eventsLoading, setEventsLoading] = useState(false);

    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

    const [operators, setOperators] = useState<EventOperatorRow[]>([]);
    const [operatorsLoading, setOperatorsLoading] = useState(false);

    const [availableUsers, setAvailableUsers] = useState<OperatorUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedRole, setSelectedRole] = useState<'ADMIN' | 'OPERATOR'>('OPERATOR');
    const [assigningOperator, setAssigningOperator] = useState(false);

    const [eventsApiMissing] = useState(() => !window.eventsAPI);

    useEffect(() => {
        if (!window.eventsAPI) return;
        void loadEvents();
        void loadUsers();
    }, [isAdmin]);

    // When user picks an event, load its operators.
    useEffect(() => {
        if (!selectedEventId) {
            setOperators([]);
            return;
        }
        void loadOperators(selectedEventId);
    }, [selectedEventId]);

    async function loadEvents(options: { keepSelection?: boolean } = {}): Promise<void> {
        const { keepSelection } = options;

        if (!window.eventsAPI) {
            const msg = 'Events API is not available';
            dispatchToast('toast-error', msg);
            return;
        }

        setEventsLoading(true);

        try {
            const res = await window.eventsAPI.listEvents({ limit: 200 });
            if (!res.ok) {
                const msg = `Failed to load events (${res.status ?? 'error'})`;
                dispatchToast('toast-error', msg);
                return;
            }

            const data = res.data as any;
            const rows: EventRow[] = Array.isArray(data?.events) ? data.events : [];
            setEvents(rows);

            if (rows.length === 0) {
                setSelectedEventId(null);
                return;
            }

            if (keepSelection && selectedEventId && rows.some((r) => r.eventId === selectedEventId)) {
                return;
            }

            if (!selectedEventId && rows.length > 0) {
                setSelectedEventId(rows[0].eventId);
            }
        } catch (err: any) {
            dispatchToastError(err, 'Failed to load events');
        } finally {
            setEventsLoading(false);
        }
    }

    async function loadOperators(eventId: string): Promise<void> {
        if (!window.eventsAPI) {
            const msg = 'Events API is not available';
            dispatchToast('toast-error', msg);
            return;
        }

        setOperatorsLoading(true);

        try {
            const res = await window.eventsAPI.listEventOperators(eventId);
            if (!res.ok) {
                const st = Number(res.status ?? 0);
                if (st === 403) {
                    setOperators([]);
                    dispatchToast(
                        'toast-error',
                        'You must be an operator (or admin) for this event to view or change operator assignments.',
                    );
                    return;
                }
                const msg = `Failed to load operators (${res.status ?? 'error'})`;
                dispatchToast('toast-error', msg);
                return;
            }

            const ops = (res.data as any)?.operators;
            setOperators(Array.isArray(ops) ? ops : []);
        } catch (err: any) {
            dispatchToastError(err, 'Failed to load operators');
        } finally {
            setOperatorsLoading(false);
        }
    }

    async function loadUsers(): Promise<void> {
        if (!window.eventsAPI || !window.eventsAPI.listUsers) {
            return;
        }

        if (!isAdmin) {
            setAvailableUsers([]);
            return;
        }

        setLoadingUsers(true);
        try {
            const res = await window.eventsAPI.listUsers();
            if (!res.ok) {
                const st = Number(res.status ?? 0);
                if (st === 403) {
                    setAvailableUsers([]);
                    return;
                }
                dispatchToast(
                    'toast-error',
                    `Failed to load users (${res.status ?? 'error'})`,
                );
                return;
            }

            const raw =
                (res.data as any)?.operators ||
                (res.data as any)?.users ||
                [];

            if (!Array.isArray(raw)) {
                return;
            }

            const mapped: OperatorUser[] = raw
                .map((u: any) => ({
                    userId: u.userId || u.sub || '',
                    preferred_username: u.preferred_username,
                    username: u.username,
                    email: u.email,
                }))
                .filter((u: OperatorUser) => !!u.userId);

            setAvailableUsers(mapped);
            if (mapped.length > 0) {
                setSelectedUserId(mapped[0].userId);
            }
        } catch (err: any) {
            dispatchToastError(err, 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    }

    async function handleAssign(): Promise<void> {
        if (!selectedEventId) {
            dispatchToast('toast-error', 'Select an event first');
            return;
        }

        if (!selectedUserId) {
            dispatchToast('toast-error', 'Select a user first');
            return;
        }

        const ops = [
            {
                userId: selectedUserId,
                roleInEvent: selectedRole,
            },
        ];

        setAssigningOperator(true);
        try {
            const res = await window.eventsAPI.assignOperators(selectedEventId, ops);

            if (!res.ok) {
                dispatchToast(
                    'toast-error',
                    `Failed to assign operator (${res.status ?? 'error'})`,
                );
                return;
            }

            dispatchToast('toast-info', 'Assigned operator to event');
            await loadOperators(selectedEventId);
        } catch (err: any) {
            dispatchToastError(err, 'Failed to assign operator');
        } finally {
            setAssigningOperator(false);
        }
    }

    function handleSelectEvent(ev: EventRow): void {
        setSelectedEventId(ev.eventId);
    }

    async function handleRefreshEvents(): Promise<void> {
        await loadEvents({ keepSelection: true });
    }

    if (eventsApiMissing) {
        return (
            <div className="px-4 py-3 text-xs text-red-700" role="alert">
                Events API is not available
            </div>
        );
    }

    return (
        <div className="px-4 py-3 space-y-4 text-xs">
            <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                            Events
                        </h3>
                        <RefreshButton
                            busy={eventsLoading}
                            onClick={() => void handleRefreshEvents()}
                            className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                            busyText="Refreshing..."
                        />
                    </div>
                    {eventsLoading && (
                        <LoadingState label="Loading events..." />
                    )}
                </div>

                {events.length === 0 && !eventsLoading ? (
                    <div className="text-[11px] text-gray-500 dark:text-slate-400">
                        No events found.
                    </div>
                ) : (
                    <TableLoadingOverlay loading={eventsLoading} label="Loading events...">
                    <div className="max-h-40 overflow-auto border border-gray-100 dark:border-slate-700 rounded bg-gray-50 dark:bg-slate-800">
                        <table className="min-w-full text-[11px]">
                            <thead className="bg-gray-100 dark:bg-slate-800 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                        Name
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                        Event ID
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                        Start
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                                {events.map((ev) => {
                                    const isSelected = selectedEventId === ev.eventId;
                                    return (
                                        <tr
                                            key={ev.eventId}
                                            onClick={() => handleSelectEvent(ev)}
                                            className={
                                                'cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700/70 ' +
                                                (isSelected ? 'bg-blue-50 dark:bg-blue-950/40' : '')
                                            }
                                        >
                                            <td className="px-3 py-2">
                                                <div className="font-medium text-gray-900 dark:text-slate-100">
                                                    {ev.name || '(untitled)'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="font-mono text-[11px] break-all text-gray-700 dark:text-slate-300">
                                                    {ev.eventId}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-slate-300">
                                                {formatTs(ev.startAt as number | undefined)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    </TableLoadingOverlay>
                )}

                <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">
                    Click a row to select the event you want to manage.
                </p>
            </div>

            <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                        Assigned operators ({operators.length})
                    </h3>
                    {operatorsLoading && selectedEventId && (
                        <span className="text-[11px] text-gray-500 dark:text-slate-400">
                            Loading operators…
                        </span>
                    )}
                </div>

                <TableLoadingOverlay loading={operatorsLoading} label="Loading operators...">
                <div className="max-h-32 overflow-auto border border-gray-100 dark:border-slate-700 rounded bg-gray-50 dark:bg-slate-800">
                    {(!selectedEventId || operators.length === 0) ? (
                        <div className="px-2 py-2 text-[11px] text-gray-500 dark:text-slate-400">
                            {selectedEventId
                                ? 'No operators assigned.'
                                : 'Select an event to view operators.'}
                        </div>
                    ) : (
                        <ul className="text-[11px] divide-y divide-gray-100 dark:divide-slate-800">
                            {operators.map((op) => (
                                <li
                                    key={op.userId}
                                    className="px-2 py-1 flex justify-between gap-2"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-gray-900 dark:text-slate-100 truncate">
                                            {formatAssignedOperatorLabel(op)}
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-slate-400 break-all">
                                            {op.preferred_username || op.email || op.username
                                                ? `${op.preferred_username || op.email || op.username} · ${op.userId}`
                                                : op.userId}
                                        </div>
                                    </div>
                                    <div className="text-right text-gray-500 dark:text-slate-400 whitespace-nowrap">
                                        {op.roleInEvent || 'OPERATOR'}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                </TableLoadingOverlay>

                <div className="mt-3 border-t border-gray-100 dark:border-slate-700 pt-3 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] text-gray-700 dark:text-slate-300">
                            Assign operator to selected event
                        </span>
                        {loadingUsers && (
                            <span className="text-[10px] text-gray-500 dark:text-slate-400">
                                Loading users…
                            </span>
                        )}
                    </div>

                    <div className="flex gap-2 items-center">
                        <select
                            className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                        >
                            {availableUsers.length === 0 && (
                                <option value="">No users available</option>
                            )}
                            {availableUsers.map((u) => (
                                <option key={u.userId} value={u.userId}>
                                    {formatUserLabel(u)}
                                </option>
                            ))}
                        </select>
                        <select
                            className="w-28 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value as 'ADMIN' | 'OPERATOR')}
                        >
                            <option value="OPERATOR">OPERATOR</option>
                            <option value="ADMIN">ADMIN</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => void handleAssign()}
                            disabled={
                                assigningOperator ||
                                !selectedEventId ||
                                !selectedUserId
                            }
                            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                            {assigningOperator ? (
                                <>
                                    <InlineSpinner size="xs" variant="current" />
                                    Assigning…
                                </>
                            ) : (
                                'Assign'
                            )}
                        </button>
                    </div>

                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                        Select an event above, choose a user and role, then click Assign.
                    </p>
                </div>
            </div>
        </div>
    );
}
